import { CustomArrowFilled } from '@qr-front/icons/custom-arrow-filled'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@qr-front/ui/components/Accordion'
import { cn } from '@qr-front/ui/utils'
import {
	DESCRIPTION_MAX_LENGTH,
	NAME_MAX_LENGTH,
} from 'constants/validationConstants'
import { RhcField } from 'features/generator/components/RhcField'
import { PrimitiveAtom, useAtom, useSetAtom } from 'jotai'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { useListener } from 'react-bus'
import { useFormContext } from 'react-hook-form'
import { TUploadedMedia } from 'services/mediaStorage'
import { maxLength } from 'utils/inputChangeUtils'

import { TAllergen } from '../Allergens/allergensConfig'
import { ImageUpload } from '../ImageUpload'
import {
	OPEN_MENU_PRODUCT_ACCORDION_EVENT,
	showProductsSortDialogAtom,
	TMenuSection,
	TMenuSectionProduct,
} from '../MenuForm'
import { TMenuFormFields } from '../menuFormModel'
import { Actions } from './Actions'
import { CLOSE_MENU_PRODUCT_ACCORDIONS_EVENT } from './Section'

const emptyImage = {
	publicId: '',
	resourceType: '',
	format: '',
	bytes: 0,
} as TUploadedMedia

const DynamicAllergens = dynamic(
	() =>
		import('../Allergens/Allergens').then((mod) => ({
			default: mod.Allergens,
		})),
	{ ssr: false },
)

const ASPECT_RATIO = 188 / 120

export type TProductHandle = {
	scrollIntoView: () => void
}

export const Product = forwardRef<
	TProductHandle,
	{
		index: number
		sectionAtom: PrimitiveAtom<TMenuSection>
		sectionIndex: number
		onProductsReorderClick: () => void
	}
>(
	(
		{
			index: productIndex,
			sectionAtom,
			sectionIndex,
			onProductsReorderClick,
		},
		ref,
	) => {
		const t = useTranslations()

		const containerRef = useRef<HTMLDivElement>(null)

		useImperativeHandle(ref, () => ({
			scrollIntoView: () => {
				containerRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			},
		}))

		const defaultAccordionValue = `product-${productIndex}`
		const [accordionValue, setAccordionValue] = useState<string>(
			defaultAccordionValue,
		)

		const form = useFormContext<TMenuFormFields>()
		const [section, setSection] = useAtom(sectionAtom)
		const products = section.products as TMenuSectionProduct[]
		const product = products?.[productIndex] as TMenuSectionProduct

		const [productImages, setProductImages] = useState<File[]>([])
		const blobUrlRef = useRef<string | null>(null)

		useEffect(() => {
			return () => {
				if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
			}
		}, [])

		const setShowSortDialog = useSetAtom(showProductsSortDialogAtom)

		const {
			allergens,
			description,
			descriptionTranslation,
			name,
			nameTranslation,
			price,
			visible,
			id,
		} = product

		const handleSetValueForField =
			(field: keyof TMenuSectionProduct) => (value?: string) => {
				setSection((section) => ({
					...section,
					products: section.products.map((product, i) => ({
						...product,
						[field]:
							i === productIndex ? value || '' : product[field],
					})),
				}))
			}

		const handleOpenAccordion = () => {
			setAccordionValue(defaultAccordionValue)
		}

		const handleProductHide = () => {
			setAccordionValue('')
			setSection((section) => ({
				...section,
				products: section.products.map((product, i) => ({
					...product,
					visible:
						i === productIndex ? !product.visible : product.visible,
				})),
			}))
		}

		const handleProductDelete = () => {
			setSection((section) => ({
				...section,
				products: section.products.filter((_, i) => i !== productIndex),
			}))
		}

		const handleToggleAllergen = (allergen: TAllergen) => {
			setSection((section) => ({
				...section,
				products: section.products.map((product, i) => {
					if (i !== productIndex) return product

					const allergenExists = product.allergens.includes(allergen)
					const updatedAllergens = allergenExists
						? product.allergens.filter((a) => a !== allergen)
						: [...product.allergens, allergen]

					return {
						...product,
						allergens: updatedAllergens,
					}
				}),
			}))
		}

		const handleProductReorder = () => {
			setShowSortDialog(true)
			onProductsReorderClick()
		}

		const handleImageUploadStart = (file: File) => {
			if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
			const url = URL.createObjectURL(file)
			blobUrlRef.current = url
			setSection((section) => ({
				...section,
				products: section.products.map((product, i) => ({
					...product,
					localImageUrl:
						i === productIndex ? url : product.localImageUrl,
				})),
			}))
		}

		const handleImageUpload = (
			uploadedFile: TUploadedMedia,
			originalFile: File,
		) => {
			if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
			blobUrlRef.current = null
			setProductImages((prev) => [...prev, originalFile])
			setSection((section) => ({
				...section,
				products: section.products.map((product, i) => ({
					...product,
					image: i === productIndex ? uploadedFile : product.image,
					localImageUrl:
						i === productIndex ? null : product.localImageUrl,
				})),
			}))
		}

		const handleImageDelete = () => {
			if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
			blobUrlRef.current = null
			setProductImages((prev) =>
				prev.filter((_, i) => i !== productIndex),
			)
			setSection((section) => ({
				...section,
				products: section.products.map((product, i) => ({
					...product,
					image: i === productIndex ? emptyImage : product.image,
					localImageUrl:
						i === productIndex ? null : product.localImageUrl,
				})),
			}))
		}

		const handleCloseAccordion = () => setAccordionValue('')

		useListener(CLOSE_MENU_PRODUCT_ACCORDIONS_EVENT, handleCloseAccordion)

		useListener(OPEN_MENU_PRODUCT_ACCORDION_EVENT, (event: unknown) => {
			const data = event as {
				sectionIndex?: number
				productIndex?: number
			}
			if (
				data?.sectionIndex === sectionIndex &&
				data?.productIndex === productIndex
			) {
				setAccordionValue(defaultAccordionValue)
			}
		})

		return (
			<div
				ref={containerRef}
				className="flex scroll-mt-16 flex-col items-start justify-center gap-2 rounded-10 bg-light-grey-80 p-4 tablet:scroll-mt-16 tablet:p-6 desktop:scroll-mt-18"
			>
				<Accordion
					disabled={!visible}
					value={accordionValue}
					onValueChange={setAccordionValue}
					type="single"
					collapsible
					className="w-full"
				>
					<AccordionItem value={accordionValue} className="w-full">
						<div className="flex flex-col gap-4">
							<div className="flex w-full items-center justify-between gap-2">
								<div className="min-w-0 flex-1">
									<AccordionTrigger
										id={defaultAccordionValue}
										icon={CustomArrowFilled}
										iconPosition="left"
										onClick={handleOpenAccordion}
										className={cn(
											'flex w-full items-center justify-start gap-2 p-0',
											'[&[data-state=closed]>svg]:-rotate-90 [&[data-state=open]>svg]:rotate-0',
											!visible && 'opacity-20',
										)}
									>
										<span className="block max-w-full overflow-hidden truncate text-ellipsis whitespace-nowrap">
											{name ||
												`${t('generator__content_form_section__menu__products__default_name')} ${id}`}
										</span>
									</AccordionTrigger>
								</div>

								<Actions
									category="product"
									disabled={!visible}
									showReorderButton={products.length > 1}
									onDelete={handleProductDelete}
									onHide={handleProductHide}
									onReorder={handleProductReorder}
								/>
							</div>
							{!visible && (
								<div className="flex items-center justify-center gap-2 rounded-full border border-dashed border-border-grey px-4 py-2 tablet:hidden">
									<span className="type-body-caption text-dark-grey">
										{t(
											'generator__content_form_section__menu__products__hidden_label',
										)}
									</span>
								</div>
							)}
						</div>

						<AccordionContent className="mt-6 flex w-full flex-col gap-6 p-0 tablet:mt-8 tablet:gap-8">
							<ImageUpload
								initialFileList={productImages}
								onUploadStart={handleImageUploadStart}
								onUpload={handleImageUpload}
								onDelete={handleImageDelete}
								aspect={ASPECT_RATIO}
							/>
							<div className="grid w-full grid-cols-1 gap-x-8 gap-y-4 tablet:grid-cols-2">
								<RhcField
									type="text"
									form={form}
									required
									path={`sections.${sectionIndex}.products.${productIndex}.name`}
									value={name}
									setValue={handleSetValueForField('name')}
									fieldName="menu_product_name"
									fieldGroup="menu_product"
									onChangeTransform={(event) =>
										maxLength(NAME_MAX_LENGTH)(event)
									}
									label={t(
										'generator__content_form_section__menu__products__name_label',
									)}
									placeholder={t(
										'generator__content_form_section__menu__products__name_placeholder',
									)}
									className="w-full tablet:col-span-2 tablet:grow"
								/>

								<RhcField
									type="text"
									form={form}
									path={`sections.${sectionIndex}.products.${productIndex}.nameTranslation`}
									value={nameTranslation}
									setValue={handleSetValueForField(
										'nameTranslation',
									)}
									fieldName="menu_product_name_translation"
									fieldGroup="menu_product"
									onChangeTransform={(event) =>
										maxLength(NAME_MAX_LENGTH)(event)
									}
									label={t(
										'generator__content_form_section__menu__products__translation_label',
									)}
									placeholder={t(
										'generator__content_form_section__menu__products__translation_placeholder',
									)}
								/>

								<RhcField
									type="text"
									form={form}
									path={`sections.${sectionIndex}.products.${productIndex}.description`}
									value={description}
									setValue={handleSetValueForField(
										'description',
									)}
									fieldName="menu_product_description"
									fieldGroup="menu_product"
									onChangeTransform={(event) =>
										maxLength(DESCRIPTION_MAX_LENGTH)(event)
									}
									label={t(
										'generator__content_form_section__menu__products__description_label',
									)}
									placeholder={t(
										'generator__content_form_section__menu__products__description_placeholder',
									)}
								/>

								<RhcField
									type="text"
									form={form}
									path={`sections.${sectionIndex}.products.${productIndex}.descriptionTranslation`}
									value={descriptionTranslation}
									setValue={handleSetValueForField(
										'descriptionTranslation',
									)}
									fieldName="menu_product_description_translation"
									fieldGroup="menu_product"
									onChangeTransform={(event) =>
										maxLength(DESCRIPTION_MAX_LENGTH)(event)
									}
									label={t(
										'generator__content_form_section__menu__products__translation_description_label',
									)}
									placeholder={t(
										'generator__content_form_section__menu__products__translation_description_placeholder',
									)}
								/>

								<RhcField
									type="text"
									form={form}
									path={`sections.${sectionIndex}.products.${productIndex}.price`}
									value={price}
									setValue={handleSetValueForField('price')}
									fieldName="menu_product_price"
									fieldGroup="menu_product"
									onChangeTransform={(event) =>
										maxLength(NAME_MAX_LENGTH)(event)
									}
									label={t(
										'generator__content_form_section__menu__products__price_label',
									)}
									placeholder={t(
										'generator__content_form_section__menu__products__price_placeholder',
									)}
								/>
							</div>

							<DynamicAllergens
								allergens={allergens as TAllergen[]}
								onClick={handleToggleAllergen}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		)
	},
)

Product.displayName = 'Product'
